import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../config/firebase';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  // Load wishlist from Firestore when user logs in
  useEffect(() => {
    if (currentUser) {
      loadWishlist();
    } else {
      setWishlistItems([]);
    }
  }, [currentUser]);

  const loadWishlist = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const wishlistRef = doc(db, 'wishlists', currentUser.uid);
      const wishlistSnap = await getDoc(wishlistRef);
      
      if (wishlistSnap.exists()) {
        const wishlistData = wishlistSnap.data();
        setWishlistItems(wishlistData.items || []);
        console.log('✅ Wishlist loaded from Firestore:', wishlistData.items?.length || 0, 'items');
      } else {
        // Create empty wishlist document
        await setDoc(wishlistRef, {
          userId: currentUser.uid,
          items: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        setWishlistItems([]);
        console.log('✅ Empty wishlist created in Firestore');
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToWishlist = async (product) => {
    if (!currentUser) {
      console.log('❌ Please log in to add items to wishlist');
      return false;
    }

    try {
      setLoading(true);
      
      // Check if product already exists in wishlist
      const existingItem = wishlistItems.find(item => item.id === product.id);
      if (existingItem) {
        console.log('⚠️ Product already in wishlist');
        return false;
      }

      const wishlistItem = {
        ...product,
        addedAt: new Date().toISOString()
      };

      // Update Firestore
      const wishlistRef = doc(db, 'wishlists', currentUser.uid);
      await updateDoc(wishlistRef, {
        items: arrayUnion(wishlistItem),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setWishlistItems(prev => [...prev, wishlistItem]);
      
      console.log('✅ Product added to wishlist:', product.name);
      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    if (!currentUser) return false;

    try {
      setLoading(true);
      
      const itemToRemove = wishlistItems.find(item => item.id === productId);
      if (!itemToRemove) {
        console.log('⚠️ Product not found in wishlist');
        return false;
      }

      // Update Firestore
      const wishlistRef = doc(db, 'wishlists', currentUser.uid);
      await updateDoc(wishlistRef, {
        items: arrayRemove(itemToRemove),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setWishlistItems(prev => prev.filter(item => item.id !== productId));
      
      console.log('✅ Product removed from wishlist:', itemToRemove.name);
      return true;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleWishlistItem = async (product) => {
    const isInWishlist = isItemInWishlist(product.id);
    
    if (isInWishlist) {
      return await removeFromWishlist(product.id);
    } else {
      return await addToWishlist(product);
    }
  };

  const isItemInWishlist = (productId) => {
    return wishlistItems.some(item => item.id === productId);
  };

  const getWishlistItemsCount = () => {
    return wishlistItems.length;
  };

  const clearWishlist = async () => {
    if (!currentUser) return false;

    try {
      setLoading(true);
      
      // Update Firestore
      const wishlistRef = doc(db, 'wishlists', currentUser.uid);
      await updateDoc(wishlistRef, {
        items: [],
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setWishlistItems([]);
      
      console.log('✅ Wishlist cleared');
      return true;
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    wishlistItems,
    loading,
    addToWishlist,
    removeFromWishlist,
    toggleWishlistItem,
    isItemInWishlist,
    getWishlistItemsCount,
    clearWishlist,
    loadWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
